import React from "react";
import { useNavigate } from "react-router-dom";
import { Palette, Users } from "lucide-react";

const MOCK_USERS = {
  agent: {
    id: "43cf63d3-b397-4416-abf5-8b31b9951ae8",
    name: "Raju Kumar",
    role: "agent",
    email: "agent@orello.in",
  },
  landlord: {
    id: "f1290777-fac0-4efa-8cbc-7cdd670dfd1d",
    name: "Suresh Iyer",
    role: "landlord",
    email: "landlord@orello.in",
  },
  tenant: {
    id: "0d39e5c1-c876-4e66-8c5f-7db2ebeb1b99",
    name: "Priya Sharma",
    role: "tenant",
    email: "tenant@orello.in",
  },
  broker_admin: {
    id: "cf704620-d92f-45b5-bd2e-dcd24728b9b1",
    name: "Sneha Reddy",
    role: "broker_admin",
    email: "broker@orello.in",
  },
};

const DASHBOARDS = [
  {
    label: "Agent Dashboard",
    role: "agent",
    path: "/app",
    mockUser: MOCK_USERS.agent,
    description: "Edit agent dashboard (Raju Kumar)",
  },
  {
    label: "Landlord Dashboard",
    role: "landlord",
    path: "/app",
    mockUser: MOCK_USERS.landlord,
    description: "Edit landlord dashboard (Suresh Iyer)",
  },
  {
    label: "Tenant Dashboard",
    role: "tenant",
    path: "/app",
    mockUser: MOCK_USERS.tenant,
    description: "Edit tenant dashboard (Priya Sharma)",
  },
  {
    label: "Broker Dashboard",
    role: "broker_admin",
    path: "/app",
    mockUser: MOCK_USERS.broker_admin,
    description: "Edit broker dashboard (Sneha Reddy)",
  },
];

export const EditPageEditor = () => {
  const nav = useNavigate();

  const handleOpenDashboard = (mockUser, path) => {
    // Store mock user in session for edit mode
    sessionStorage.setItem("editMode", "true");
    sessionStorage.setItem("editModeUser", JSON.stringify(mockUser));

    // Navigate to dashboard with edit mode
    nav(`${path}?editMode=true`);
  };

  return (
    <div className="w-full flex flex-col text-gray-900">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4 bg-white">
        <div className="flex items-center gap-3 mb-2">
          <Palette size={24} className="text-blue-600" />
          <h1 className="text-2xl font-bold">Page Editor</h1>
        </div>
        <p className="text-sm text-gray-600">
          Click a dashboard to edit it in real-time with mock user accounts
        </p>
      </div>

      {/* Dashboard Selection */}
      <div className="flex-1 p-6 bg-gray-50 overflow-y-auto">
        <div className="max-w-4xl">
          <div className="flex items-center gap-2 mb-6">
            <Users size={20} className="text-gray-600" />
            <h2 className="text-lg font-bold text-gray-700">Select Dashboard</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {DASHBOARDS.map((dashboard) => (
              <button
                key={dashboard.role}
                onClick={() =>
                  handleOpenDashboard(dashboard.mockUser, dashboard.path)
                }
                className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-600 hover:shadow-lg transition text-left group"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600">
                    {dashboard.label}
                  </h3>
                  <div className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                    Edit Mode
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  {dashboard.description}
                </p>

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Mock User: <span className="font-medium text-gray-700">{dashboard.mockUser.name}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {dashboard.mockUser.email}
                  </p>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-blue-600 font-medium">
                    → Click to open in edit mode
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Info Box */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-bold text-blue-900 mb-2">ℹ️ How to use:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Click a dashboard card to open it in edit mode</li>
              <li>• You'll be logged in as the mock user for that role</li>
              <li>• Click "Edit Page" button on the dashboard to make changes</li>
              <li>• Edit sections inline - colors, text, images</li>
              <li>• Click "Save Draft" → "Publish" to apply changes</li>
              <li>• Exit edit mode to return to normal view</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPageEditor;
